"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
function getFirstImageFromContent(content?: string): {
  src: string;
  alt?: string;
} | null {
  if (!content) return null;
  const imgMatch = content.match(/<img\b([^>]*?)>/i);
  if (!imgMatch) return null;
  const attrs = imgMatch[1];
  const srcMatch = attrs.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
  if (!srcMatch || !srcMatch[1]) return null;
  const rawSrc = srcMatch[1].trim();
  if (!rawSrc) return null;
  const src = rawSrc.replace(/&amp;/g, "&");
  const altMatch = attrs.match(/\balt\s*=\s*["']([^"']*)["']/i);
  const alt = altMatch && altMatch[1] ? altMatch[1].trim() : undefined;
  return {
    src,
    alt
  };
}
function UrbanStoriesGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-6">
            <div className="aspect-[4/5] w-full bg-muted/50 animate-pulse" />
            <div className="h-5 w-24 bg-muted/50 animate-pulse" />
            <div className="h-8 w-3/4 bg-muted/50 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>Failed to load stories. Please try again later.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>No stories available at the moment.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const numeral = `0${i + 1}`;
      const placeholderImg = `/img/luxury_travel_hub_${i + 1}.png`;
      const contentImg = getFirstImageFromContent(post.content);
      const imgSrc = post.featuredImage || contentImg?.src || placeholderImg;
      const imgAlt = imgSrc === contentImg?.src && contentImg?.alt ? contentImg.alt : post.title;
      return <Card key={post.id} data-index={i} className="group flex flex-col gap-6 rounded-md border-none bg-transparent p-0 shadow-md transition-all duration-500 hover:shadow-lg">
            <Link to={post.link || "#"} className="block w-full overflow-hidden [perspective:1000px]">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                <img src={imgSrc} alt={imgAlt} onError={e => {
              if (e.currentTarget.src !== placeholderImg && !e.currentTarget.src.endsWith(placeholderImg)) {
                e.currentTarget.src = placeholderImg;
              }
            }} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute top-4 left-4 pointer-events-none">
                  <span className="font-serif text-6xl font-light opacity-20 text-foreground">
                    {numeral}
                  </span>
                </div>
              </div>
            </Link>
            
            <CardHeader className="p-0">
              <div className="mb-3 flex items-center">
                <Badge variant="secondary" className="rounded-md px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-secondary-foreground bg-secondary/50 backdrop-blur-sm">
                  {post.categories?.[0]?.name || "Travel Insight"}
                </Badge>
              </div>
              <CardTitle className="font-serif text-2xl font-medium leading-tight tracking-tight text-foreground">
                <Link to={post.link || "#"} className="relative inline-block after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-foreground after:transition-all after:duration-300 group-hover:after:w-full">
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="mt-auto p-0">
              <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground transition-colors hover:text-muted-foreground">
                Read More
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function TheHubHighlights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="238807" id="the-hub-highlights" className="bg-background py-24 text-foreground lg:py-32">
      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-light tracking-tight md:text-5xl lg:text-6xl">
              The Hub
            </h2>
            <p className="mt-4 font-default text-lg text-muted-foreground leading-relaxed">
              A curated collection of solo traveler insights, expert knowledge sharing, and exclusive reviews for the premium LGBTQ+ community.
            </p>
          </div>
          <Link className="inline-flex items-center gap-2 border-b border-foreground/20 pb-1 text-sm font-bold uppercase tracking-widest transition-all hover:border-foreground hover:text-foreground" to="/TheHubPage">
            Explore All Insights
            <ArrowRight className="size-4" />
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <UrbanStoriesGrid />
        </PostsProvider>
      </div>
    </section>;
}